// src/services/definitionOfDoneService.ts
//
// Ch76 · client for GET /admin/definition-of-done
// (backend/api/routes/admin_dod.py -> backend/api/services/definition_of_done.py).
//
// The Definition of Done (RATIFIED 2026-09-01): all eight rows green
// simultaneously for 30 consecutive days, each read by an instrument. Every
// row arrives as {row, condition, status, evidence, definition_id, read_at,
// note?} where status is green | red | unknown. `unknown` carries the REASON
// (reader failed, or the instrument lives outside LN Pro) and MUST render as
// the word — never as a verdict. Same envelope rule as adminTruthService.

import { fetchWithAuth } from "../lib/api";

export type DodStatus = "green" | "red" | "unknown";

export interface DodRow {
  row: string;
  condition: string;
  status: DodStatus;
  evidence: Record<string, unknown>;
  definition_id: string;
  read_at: string;
  note?: string;
}

export interface DodOverall {
  all_green_today: boolean;
  green_rows: string[];
  red_rows: string[];
  unknown_rows: string[];
  measured_rows: string[];
  required_consecutive_days: number;
  /** "ok" when the series was read; "unknown" when it could not be (then the counts are null). */
  streak_status: "ok" | "unknown";
  consecutive_green_days: number | null;
  streak_start: string | null;
  done?: boolean;
  note?: string;
}

export interface DodSnapshotStatus {
  status: "ok" | "unknown";
  today: string;
  latest_date: string | null;
  today_exists: boolean | null;
  note?: string;
}

export interface DefinitionOfDoneResponse {
  definition: { source: string; ratified_on: string; rule: string };
  rows: DodRow[];
  overall: DodOverall;
  snapshot: DodSnapshotStatus;
  generated_at: string;
}

export async function fetchDefinitionOfDone(): Promise<DefinitionOfDoneResponse> {
  const res = await fetchWithAuth<DefinitionOfDoneResponse>("/admin/definition-of-done");
  // The service is the shape guard (same rule as adminTruthService): a 200
  // whose body is not the contract becomes a rejection here, not a render throw.
  if (
    !res ||
    typeof res !== "object" ||
    !Array.isArray((res as any).rows) ||
    typeof (res as any).overall !== "object" ||
    (res as any).overall === null
  ) {
    throw new Error("Malformed definition-of-done response: `rows`/`overall` missing");
  }
  return res;
}
